import { MaybeTimestamp, toTimestamp } from "@/models/MaybeTimestamp"
import {
  basePriceString,
  getPricingString,
  isTemporaryPrice,
  Price,
} from "@/models/Price"
import { PricePeriod } from "@/models/PricePeriod"
import { PricePeriodUnit } from "@/models/PricePeriodUnit"
import { PriceChangeDetails } from "@/models/PriceChangeDetails"
import { format, isToday, isYesterday } from "date-fns"
import { z } from "zod"
import { DateLike, toDate, toUtcDate, toUtcDateOrNull } from "@/support/dates"

export const zSubscriptionDisplayState = z.enum([
  "overdue",
  "upcoming",
  "expiring",
  "active",
  "completed",
  "incomplete",
  "pending",
  "paused",
  "expired",
  "failed",
])

export const SubscriptionDisplayState = zSubscriptionDisplayState.enum
export type SubscriptionDisplayState = z.infer<typeof zSubscriptionDisplayState>

export const activeDisplayStates: SubscriptionDisplayState[] = [
  "overdue",
  "upcoming",
  "active",
  "paused",
]

export const nonActiveDisplayStates: SubscriptionDisplayState[] = [
  // expiring or expired with any failed payments for an installment plan
  "incomplete",
  "completed",
  "expired",
  "expiring",
  "failed",
  // If the user started the purchase process but cancelled we get
  // a pending subscription (i.e. an incomplete payment_intent).
  // In this case, since they never completed the purchase we can ignore it.
  // It will eventually get marked as failed.
  "pending",
]

export const isActiveSub = (subscription: Subscription): boolean => {
  const displayStates = subscription.displayStates ?? []
  const isVisible = subscription.isVisible ?? false
  return (
    isVisible &&
    displayStates.some((state) => activeDisplayStates.includes(state))
  )
}

const displayStateSort = {
  [SubscriptionDisplayState.overdue]: 0,
  [SubscriptionDisplayState.upcoming]: 1,
  [SubscriptionDisplayState.expiring]: 2,
  [SubscriptionDisplayState.active]: 3,
  [SubscriptionDisplayState.completed]: 4,
  [SubscriptionDisplayState.incomplete]: 5,
  [SubscriptionDisplayState.pending]: 6,
  [SubscriptionDisplayState.paused]: 7,
  [SubscriptionDisplayState.expired]: 8,
}

export const zSubscriptionStatus = z.enum([
  "upcoming",
  "active",
  "paused",
  "overdue",
  "expiring",
  "expired",
  "pending",
  "complete",
  "incomplete",
  // note: iOS doesn't have this state, but it is valid
  "failed",
])

export const SubscriptionStatus = zSubscriptionStatus.enum

export type SubscriptionStatus = z.infer<typeof zSubscriptionStatus>

export interface InstallmentPlanMetadata {
  complete: number
  remaining: number
  total: number
}

export interface InstallmentPlan {
  completed: boolean
  installments: InstallmentPlanMetadata
}

export interface ScheduledActionArgs {
  until?: MaybeTimestamp
}

export interface ScheduledAction {
  actionID: string
  at: MaybeTimestamp
  type: string
  args: ScheduledActionArgs
}

export interface ScheduledActions {
  PAUSE?: ScheduledAction
  CANCEL?: ScheduledAction
  RESUME?: ScheduledAction
}

export interface OfferingPreview {
  name: string
  description?: string
  isPreview?: boolean
  status?: string
  offeringID?: string
  permissions?: {
    customerCanPause: boolean
    customerCanCancel: boolean
  }
}

export interface UserPreview {
  name: string
  email: string
  profileImageURL?: string
  userID: string
}

export interface OfferingUserPreview {
  userID: string
  name: string
  email?: string
  profileImageURL?: string
  subtitle?: string
}

export interface PriceProgress {
  current: {
    completed?: boolean
    cycles?: number
    from: MaybeTimestamp
    passed: number
    period: {
      duration: number
      unit: string
    }
    remaining?: number
    until?: MaybeTimestamp
  }
  next?: {
    completed?: boolean
    cycles?: number
    from: MaybeTimestamp
    passed: number
    period: {
      duration: number
      unit: string
    }
    remaining?: number
    until?: MaybeTimestamp
  }
}

export interface Subscription {
  userID: string
  offeringPreview: OfferingPreview | null | undefined
  offeringUserPreview: OfferingUserPreview
  userPreview: UserPreview
  price: Price
  basePrice: Price
  invoiceStatus?: string
  agreementID?: string
  subscriptionID: string
  offeringID: string
  displayStates?: SubscriptionDisplayState[]
  priceChangeDetails?: PriceChangeDetails
  nextChargeAt?: MaybeTimestamp | null
  renewsAt?: MaybeTimestamp | null
  renewedAt?: MaybeTimestamp | null
  expiresAt?: MaybeTimestamp
  billingRetriesAt?: MaybeTimestamp
  startAt?: MaybeTimestamp
  unpauseAt?: MaybeTimestamp
  // DO NOT USE => use displayStates
  status?: SubscriptionStatus
  isVisible?: boolean
  scheduledActions?: ScheduledActions
  installmentPlan?: InstallmentPlan
  priceProgress?: PriceProgress
  temporaryPrice?: Price
  requestedCancellationAt?: MaybeTimestamp
  createdAt: MaybeTimestamp
  pausedAt?: MaybeTimestamp
  failedInvoiceCount?: number
  failedRequiringAttentionCount?: number

  paidRenewalAt?: MaybeTimestamp | null
  resumesAt?: MaybeTimestamp | null
}

// region: Logic from iOS Subscription.swift
export function getCurrentPricingString(subscription: Subscription): string {
  const { price, priceChangeDetails } = subscription
  const period = price.period

  if (price.type === "FIXED_TERM" && period) {
    const priceChange = priceChangeDetails != null
    const hasTemporaryPrice = isTemporaryPrice(price)

    if (priceChange && price.defaultEffectiveCycles) {
      if (hasTemporaryPrice) {
        // If it's a temporary price, just return the base price string
        return basePriceString(price, period)
      } else {
        // If it's not temporary, include the number of payments
        const plural = price.defaultEffectiveCycles === 1 ? "" : "s"
        return `${basePriceString(
          price,
          period,
        )} for ${price.defaultEffectiveCycles} payment${plural}`
      }
    } else {
      // No price change, return standard pricing string
      return getPricingString(price)
    }
  } else {
    // Not a fixed term price, return standard pricing string
    return getPricingString(price)
  }
}

function getTemporaryPriceRemainingDescription(
  subscription: Subscription,
): string | null {
  const { basePrice, priceProgress, displayStates = [] } = subscription
  const status = displayStates[0]

  if (basePrice == null) return null

  const remainingPriceChangeCycles = priceProgress?.current.remaining
  if (remainingPriceChangeCycles == null) return null
  if (remainingPriceChangeCycles <= 0) return null
  if (status === SubscriptionDisplayState.expired) return null
  if (status === SubscriptionDisplayState.expiring) return null

  const hasFuturePayment = priceProgress?.next != null

  return `${remainingPriceChangeCycles} payment${
    remainingPriceChangeCycles > 1 ? "s" : ""
  } remaining${hasFuturePayment ? ` then ${getPricingString(basePrice)}` : ""}`
}

// Helper to get iterations remaining
function getIterationsRemaining(subscription: Subscription): number | null {
  const priceProgress = subscription.priceProgress
  if (priceProgress == null) return null

  const remaining = priceProgress.current.remaining
  if (remaining == null) return null

  return remaining + (priceProgress.next?.remaining ?? 0)
}

// matches the iOS login in Subscription.swift
export function getPriceSummaryDescription(
  subscription: Subscription,
): string | null {
  // clog({ subscription })
  const { price, displayStates = [], basePrice, priceProgress } = subscription
  const status = displayStates[0]

  // Only proceed if this is a fixed term price
  if (price.type !== "FIXED_TERM") {
    // Not a fixed term price, return temporary price description
    return getTemporaryPriceRemainingDescription(subscription)
  }

  const totalRemainingPayments = getIterationsRemaining(subscription) ?? 0
  const remainingPriceChangeCycles = priceProgress?.current.remaining

  const guardCondition =
    basePrice != null &&
    remainingPriceChangeCycles != null &&
    status !== SubscriptionDisplayState.expired &&
    status !== SubscriptionDisplayState.expiring

  if (!guardCondition) {
    if (totalRemainingPayments > 0) {
      return `${totalRemainingPayments} payment${totalRemainingPayments > 1 ? "s" : ""} remaining`
    }
    return null
  }

  const next = priceProgress?.next
  if (next?.remaining) {
    // We have a next period with remaining payments
    const nextRemaining = next.remaining
    const period: PricePeriod = {
      duration: next.period.duration,
      unit: next.period.unit.toLowerCase() as PricePeriodUnit,
      recurring: true,
    }
    return `${remainingPriceChangeCycles} payment${
      remainingPriceChangeCycles > 1 ? "s" : ""
    } remaining then ${basePriceString(basePrice, period)} for ${nextRemaining} payments`
  }

  if (totalRemainingPayments > 0) {
    return `${totalRemainingPayments} payment${totalRemainingPayments > 1 ? "s" : ""} remaining`
  }

  return null
}

// endregion

export function hasDisplayState(
  subscription: Subscription,
  subscriptionDisplayState: SubscriptionDisplayState,
): boolean {
  const displayStates = subscription.displayStates || []
  return displayStates.includes(subscriptionDisplayState)
}

export function sortDisplayStates(displayStates: SubscriptionDisplayState[]) {
  return displayStates.sort((a, b) => displayStateSort[a] - displayStateSort[b])
}

export function formatPaymentStatusDescriptionTimestamp(
  maybeTimestamp: MaybeTimestamp,
): string {
  const date = toDate(maybeTimestamp)
  if (isToday(date)) {
    return "Today"
  }

  if (isYesterday(date)) {
    return "Yesterday"
  }

  return format(date, "d LLL yyyy")
}

export function formatPaymentStatusDescriptionTimestampDayOfWeekFmt(
  maybeTimestamp: MaybeTimestamp,
): string {
  return format(toDate(maybeTimestamp), "EEE, d MMM")
}

export function formatPaymentStatusDescriptionTimestampShort(
  maybeTimestamp: MaybeTimestamp,
): string {
  return format(toDate(maybeTimestamp), "d LLL")
}

export function nextStatusChange(subscription: Subscription): number {
  const t =
    subscription.renewsAt ||
    subscription.expiresAt ||
    subscription.nextChargeAt ||
    subscription.unpauseAt ||
    subscription.billingRetriesAt
  return ((t && toTimestamp(t).seconds) || Date.now()) / 1000
}

export function sortSubscriptions(subscriptions: Subscription[]) {
  const secondsNow = Date.now() / 1000
  const cloned = [...subscriptions]
  return cloned.sort((a, b) => {
    return (
      Math.abs(nextStatusChange(a) - secondsNow) -
      Math.abs(nextStatusChange(b) - secondsNow)
    )
  })
}

export function displayStatesLookup(displayStates: string[] = []) {
  return Object.fromEntries(displayStates.map((s) => [s, true]))
}

export function allDisplayStatesDistinctSorted(
  subscriptions: Subscription[],
): SubscriptionDisplayState[] {
  const allDisplayStates = subscriptions.flatMap(
    (subscription: Subscription) => subscription.displayStates ?? [],
  )
  const allDisplayStatesSet = new Set<SubscriptionDisplayState>(
    allDisplayStates,
  )
  const allDisplayStatesDistinct: SubscriptionDisplayState[] = []
  allDisplayStatesSet.forEach((subscriptionDisplayState) =>
    allDisplayStatesDistinct.push(subscriptionDisplayState),
  )
  return sortDisplayStates(allDisplayStatesDistinct)
}

/**
 * Indicates if the subscription is paused at the provided date
 */
export function isScheduledPause(
  subscription: Subscription,
  dateLike: DateLike,
): boolean {
  const { scheduledActions } = subscription
  const date = toUtcDate(dateLike)

  // Check if there's a scheduled pause
  const pauseStart = toUtcDateOrNull(scheduledActions?.PAUSE?.at)
  const pauseEnd = toUtcDateOrNull(scheduledActions?.PAUSE?.args?.until)
  if (!pauseStart || !pauseEnd) {
    return false
  }

  // Check if the date falls within the pause range
  return date >= pauseStart && date <= pauseEnd
}
